"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState, useSyncExternalStore } from "react";
import { LandingButton } from "../button/landing-button";
import { type HeroAction, type HeroPage, nextHeroPage } from "./hero-pages";

const PAGE_MS = 6000;

const REDUCED = "(prefers-reduced-motion: reduce)";

function useReducedMotion() {
  return useSyncExternalStore(
    (notify) => {
      const query = matchMedia(REDUCED);
      query.addEventListener("change", notify);
      return () => query.removeEventListener("change", notify);
    },
    () => matchMedia(REDUCED).matches,
    () => false,
  );
}

/** A hand-drawn wavy stroke (not a straight rule), used as a mask so it takes the text colour. It stretches to the width of the highlighted word. */
const SCRIBBLE = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 14" preserveAspectRatio="none"><path d="M3 9 C 24 2, 40 13, 66 7 S 112 3, 136 9 S 172 12, 197 5" fill="none" stroke="white" stroke-width="5" stroke-linecap="round" vector-effect="non-scaling-stroke"/></svg>')}")`;

/** The headline; the word between stars is underlined by hand, with a wavy stroke. */
function Headline({ text }: { text: string }) {
  return (
    <h1 className="text-5xl font-black leading-[1.1] tracking-tighter text-balance [overflow-wrap:anywhere] sm:text-6xl xl:text-7xl">
      {text.split("*").map((part, i) => (i % 2 ? <mark key={i} className="relative bg-transparent pb-[0.2em] text-inherit after:absolute after:inset-x-0 after:bottom-0 after:h-[0.3em] after:bg-current after:[mask-image:var(--scribble)] after:[mask-size:100%_100%] after:content-['']" style={{ "--scribble": SCRIBBLE } as React.CSSProperties}>{part}</mark> : part))}
    </h1>
  );
}

/** One dot per page and "n / total". */
function HeroControls({ count, current, onSelect }: { count: number; current: number; onSelect: (page: number) => void }) {
  const t = useTranslations("hero");
  if (count < 2) return null;
  return (
    <div className="flex items-center gap-4 text-sm">
      <div className="flex flex-wrap gap-2.5">
        {Array.from({ length: count }, (_, i) => (
          <button key={i} type="button" aria-label={t("page", { number: i + 1 })} aria-current={i === current} onClick={() => onSelect(i)} className="size-2.5 rounded-full bg-brand-foreground/40 transition-transform hover:bg-brand-foreground/70 aria-[current=true]:scale-125 aria-[current=true]:bg-brand-foreground" />
        ))}
      </div>
      <span aria-hidden>{current + 1} / {count}</span>
    </div>
  );
}

/** The text-led hero on the brand colour: one headline at a time, changing every few seconds without stopping, unless the visitor prefers reduced motion. The coded homepage and the Hero block of the Pages app both draw it. */
export function HeroView({ pages, actions = [] }: { pages: HeroPage[]; actions?: HeroAction[] }) {
  const [page, setPage] = useState(0);
  const reduced = useReducedMotion();
  const playing = !reduced;
  useEffect(() => {
    if (!playing || pages.length < 2) return;
    const timer = setTimeout(() => setPage((p) => nextHeroPage(p, pages.length)), PAGE_MS);
    return () => clearTimeout(timer);
  }, [playing, page, pages.length]);
  const { title, body, art } = pages[page];
  return (
    <section className="overflow-x-clip bg-brand px-6 py-16 text-brand-foreground sm:py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[minmax(0,1fr)_28rem]">
        <div className="flex min-h-[26rem] min-w-0 flex-col justify-between gap-10">
          <div key={page} className="hero-swap flex flex-col gap-6">
            <Headline text={title} />
            {body && <p className="max-w-md text-lg text-brand-foreground/85">{body}</p>}
            {actions.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {actions.map((action, i) => <LandingButton key={action.href} size="lg" className={i === 0 ? "border-2 border-brand-foreground bg-brand-foreground text-brand hover:bg-brand-foreground/85" : "border-2 border-brand-foreground bg-transparent text-brand-foreground hover:bg-brand-foreground/15"} {...action} />)}
              </div>
            )}
          </div>
          <HeroControls count={pages.length} current={page} onSelect={setPage} />
        </div>
        {art && <div key={page} className="hidden size-[28rem] lg:block">{art}</div>}
      </div>
    </section>
  );
}
